import React from 'react';

export const IllustrationDemo = ({ colors }) => {
    const getColor = (index) => colors[index]?.value || colors[0]?.value || '#3b82f6';

    // Convert hex to RGB for transparency effects
    const hexToRgba = (hex, alpha = 1) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return hex;
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    return (
        <div className="space-y-8">
            <div className="text-center">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Graphical Elements Library</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Comprehensive library of illustrations, icons, badges, and graphic elements using your color palette.
                </p>
            </div>

            {/* Geometric Illustrations */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Geometric Compositions</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Abstract Geometric Scene */}
                    <div className="relative h-40 rounded-lg overflow-hidden" style={{ backgroundColor: hexToRgba(getColor(4), 0.1) }}>
                        <svg className="w-full h-full" viewBox="0 0 200 160">
                            {/* Background shapes */}
                            <circle cx="160" cy="40" r="25" fill={hexToRgba(getColor(0), 0.8)} />
                            <rect x="20" y="60" width="50" height="30" rx="15" fill={hexToRgba(getColor(1), 0.9)} />
                            <polygon points="120,100 140,120 100,120" fill={hexToRgba(getColor(2), 0.7)} />
                            <ellipse cx="60" cy="30" rx="20" ry="15" fill={hexToRgba(getColor(3), 0.6)} />

                            {/* Connecting lines */}
                            <path d="M60,45 Q90,70 120,100" stroke={getColor(1)} strokeWidth="2" fill="none" strokeDasharray="5,5" opacity="0.6" />
                            <line x1="70" y1="60" x2="160" y2="40" stroke={getColor(0)} strokeWidth="1.5" opacity="0.4" />

                            {/* Small accent dots */}
                            <circle cx="90" cy="50" r="3" fill={getColor(2)} />
                            <circle cx="150" cy="90" r="4" fill={getColor(3)} />
                            <circle cx="40" cy="110" r="2" fill={getColor(0)} />
                        </svg>
                    </div>

                    {/* Isometric Illustration */}
                    <div className="relative h-40 rounded-lg overflow-hidden" style={{ backgroundColor: hexToRgba(getColor(0), 0.05) }}>
                        <svg className="w-full h-full" viewBox="0 0 200 160">
                            {/* Isometric cubes */}
                            <g transform="translate(100,80)">
                                {/* Main cube */}
                                <path d="M-20,-10 L0,-20 L20,-10 L20,10 L0,20 L-20,10 Z" fill={getColor(0)} />
                                <path d="M-20,-10 L-20,10 L0,20 L0,0 Z" fill={hexToRgba(getColor(0), 0.7)} />
                                <path d="M0,0 L0,20 L20,10 L20,-10 Z" fill={hexToRgba(getColor(0), 0.5)} />

                                {/* Secondary cubes */}
                                <g transform="translate(-30,-15)">
                                    <path d="M-10,-5 L0,-10 L10,-5 L10,5 L0,10 L-10,5 Z" fill={getColor(1)} />
                                    <path d="M-10,-5 L-10,5 L0,10 L0,0 Z" fill={hexToRgba(getColor(1), 0.7)} />
                                    <path d="M0,0 L0,10 L10,5 L10,-5 Z" fill={hexToRgba(getColor(1), 0.5)} />
                                </g>

                                <g transform="translate(30,-5)">
                                    <path d="M-8,-4 L0,-8 L8,-4 L8,4 L0,8 L-8,4 Z" fill={getColor(2)} />
                                    <path d="M-8,-4 L-8,4 L0,8 L0,0 Z" fill={hexToRgba(getColor(2), 0.7)} />
                                    <path d="M0,0 L0,8 L8,4 L8,-4 Z" fill={hexToRgba(getColor(2), 0.5)} />
                                </g>
                            </g>
                        </svg>
                    </div>

                    {/* Flowing Organic Shape */}
                    <div className="relative h-40 rounded-lg overflow-hidden" style={{ backgroundColor: hexToRgba(getColor(2), 0.08) }}>
                        <svg className="w-full h-full" viewBox="0 0 200 160">
                            <defs>
                                <linearGradient id="organic-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor={getColor(2)} />
                                    <stop offset="50%" stopColor={getColor(3)} />
                                    <stop offset="100%" stopColor={getColor(1)} />
                                </linearGradient>
                            </defs>

                            <path
                                d="M40,120 Q60,40 100,60 Q140,80 160,40 Q180,20 190,60 Q185,100 150,110 Q110,130 80,100 Q50,140 40,120 Z"
                                fill="url(#organic-gradient)"
                                opacity="0.8"
                            />

                            {/* Accent shapes */}
                            <ellipse cx="80" cy="80" rx="6" ry="12" fill={hexToRgba(getColor(4), 0.6)} />
                            <ellipse cx="130" cy="70" rx="4" ry="8" fill={hexToRgba(getColor(0), 0.5)} />
                            <circle cx="110" cy="90" r="3" fill="white" opacity="0.8" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Icon Families */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Icon Systems</h5>
                <div className="space-y-6">
                    {/* Outlined Icons */}
                    <div>
                        <h6 className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Outlined Style</h6>
                        <div className="grid grid-cols-6 md:grid-cols-12 gap-3">
                            {[
                                // Home
                                "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
                                // Heart
                                "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z",
                                // Star
                                "M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z",
                                // Settings
                                "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
                                // Mail
                                "M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
                                // User
                                "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            ].map((path, index) => (
                                <div key={index} className="flex items-center justify-center w-10 h-10 rounded-lg" style={{ backgroundColor: hexToRgba(getColor(index % colors.length), 0.1) }}>
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke={getColor(index % colors.length)} strokeWidth="1.5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
                                    </svg>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Filled Icons */}
                    <div>
                        <h6 className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Filled Style</h6>
                        <div className="grid grid-cols-6 md:grid-cols-12 gap-3">
                            {[
                                // Home filled
                                "M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z",
                                // Heart filled
                                "M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z",
                                // Star filled
                                "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z",
                                // Bell
                                "M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z",
                                // Search
                                "M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z",
                                // Plus
                                "M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                            ].map((path, index) => (
                                <div key={index} className="flex items-center justify-center w-10 h-10 rounded-lg" style={{ backgroundColor: hexToRgba(getColor(index % colors.length), 0.15) }}>
                                    <svg className="w-5 h-5" viewBox="0 0 20 20" fill={getColor(index % colors.length)}>
                                        <path fillRule="evenodd" d={path} clipRule="evenodd" />
                                    </svg>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Infographic Elements */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Data Visualization Elements</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Progress Charts */}
                    <div>
                        <h6 className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Progress Indicators</h6>
                        <div className="space-y-4">
                            {[
                                { label: 'Design System', value: 85 },
                                { label: 'Development', value: 70 },
                                { label: 'Testing', value: 45 },
                                { label: 'Deployment', value: 25 }
                            ].map((item, index) => (
                                <div key={index} className="flex items-center gap-3">
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">{item.label}</span>
                                            <span className="text-xs text-gray-500">{item.value}%</span>
                                        </div>
                                        <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700">
                                            <div
                                                className="h-2 rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${item.value}%`,
                                                    backgroundColor: getColor(index % colors.length)
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Circular Progress */}
                    <div>
                        <h6 className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Circular Charts</h6>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { label: 'Performance', value: 78 },
                                { label: 'Accessibility', value: 92 },
                                { label: 'Best Practices', value: 85 },
                                { label: 'SEO', value: 96 }
                            ].map((item, index) => (
                                <div key={index} className="text-center">
                                    <div className="relative w-16 h-16 mx-auto mb-2">
                                        <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
                                            <circle
                                                cx="32"
                                                cy="32"
                                                r="28"
                                                strokeWidth="6"
                                                stroke="currentColor"
                                                fill="transparent"
                                                className="text-gray-200 dark:text-gray-700"
                                            />
                                            <circle
                                                cx="32"
                                                cy="32"
                                                r="28"
                                                strokeWidth="6"
                                                stroke={getColor(index % colors.length)}
                                                fill="transparent"
                                                strokeDasharray={`${(item.value / 100) * 175.929} 175.929`}
                                                className="transition-all duration-500"
                                            />
                                        </svg>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-xs font-medium text-gray-900 dark:text-white">{item.value}</span>
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">{item.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Decorative Elements */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Decorative Elements</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Pattern Backgrounds */}
                    <div>
                        <h6 className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Pattern Textures</h6>
                        <div className="grid grid-cols-2 gap-2">
                            {colors.slice(0, 4).map((color, index) => (
                                <div key={index} className="h-16 rounded-lg overflow-hidden">
                                    <svg className="w-full h-full" viewBox="0 0 100 100">
                                        <defs>
                                            <pattern id={`texture-${index}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                                {index === 0 && <circle cx="10" cy="10" r="2" fill={color.value} opacity="0.6" />}
                                                {index === 1 && <rect x="8" y="8" width="4" height="4" fill={color.value} opacity="0.5" />}
                                                {index === 2 && <polygon points="10,6 14,14 6,14" fill={color.value} opacity="0.7" />}
                                                {index === 3 && <path d="M0,10 L20,10 M10,0 L10,20" stroke={color.value} strokeWidth="1" opacity="0.4" />}
                                            </pattern>
                                        </defs>
                                        <rect width="100" height="100" fill={hexToRgba(color.value, 0.1)} />
                                        <rect width="100" height="100" fill={`url(#texture-${index})`} />
                                    </svg>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Divider Elements */}
                    <div>
                        <h6 className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Section Dividers</h6>
                        <div className="space-y-4">
                            {colors.slice(0, 3).map((color, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <div className="flex-1 h-px" style={{ backgroundColor: hexToRgba(color.value, 0.3) }} />
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color.value }} />
                                    <div className="flex-1 h-px" style={{ backgroundColor: hexToRgba(color.value, 0.3) }} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Accent Shapes */}
                    <div>
                        <h6 className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Accent Shapes</h6>
                        <div className="grid grid-cols-3 gap-2">
                            {colors.slice(0, 6).map((color, index) => (
                                <div key={index} className="h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: hexToRgba(color.value, 0.1) }}>
                                    <div
                                        className="w-6 h-6 rounded-full"
                                        style={{
                                            backgroundColor: color.value,
                                            clipPath: index % 3 === 0 ? 'polygon(50% 0%, 0% 100%, 100% 100%)' :
                                                index % 3 === 1 ? 'polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)' :
                                                    'none'
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Abstract Blob Shapes */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Abstract Blob Shapes</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {colors.slice(0, 4).map((color, index) => (
                        <div key={index} className="aspect-square rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: hexToRgba(color.value, 0.08) }}>
                            <svg className="w-full h-full p-4" viewBox="0 0 200 200">
                                <defs>
                                    <linearGradient id={`blob-grad-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor={color.value} stopOpacity="0.9" />
                                        <stop offset="100%" stopColor={color.value} stopOpacity="0.6" />
                                    </linearGradient>
                                </defs>
                                {index === 0 && (
                                    <path d="M100,20 Q150,50 170,100 Q150,150 100,180 Q50,150 30,100 Q50,50 100,20 Z" fill={`url(#blob-grad-${index})`} />
                                )}
                                {index === 1 && (
                                    <path d="M100,10 Q160,40 180,100 Q140,170 80,190 Q20,150 10,100 Q40,30 100,10 Z" fill={`url(#blob-grad-${index})`} />
                                )}
                                {index === 2 && (
                                    <path d="M100,30 Q160,60 175,120 Q130,180 70,180 Q20,130 25,80 Q60,25 100,30 Z" fill={`url(#blob-grad-${index})`} />
                                )}
                                {index === 3 && (
                                    <ellipse cx="100" cy="100" rx="80" ry="70" fill={`url(#blob-grad-${index})`} transform="rotate(30 100 100)" />
                                )}
                            </svg>
                        </div>
                    ))}
                </div>
            </div>

            {/* Badges and Stickers */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Badges & Stickers</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {colors.slice(0, 4).map((color, index) => (
                        <div key={index} className="flex flex-col items-center gap-3">
                            {/* Circular Badge */}
                            <div className="relative w-20 h-20">
                                <svg className="w-full h-full" viewBox="0 0 100 100">
                                    <defs>
                                        <linearGradient id={`badge-grad-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor={color.value} />
                                            <stop offset="100%" stopColor={hexToRgba(color.value, 0.7)} />
                                        </linearGradient>
                                    </defs>
                                    <circle cx="50" cy="50" r="45" fill={`url(#badge-grad-${index})`} />
                                    <circle cx="50" cy="50" r="38" fill="none" stroke="white" strokeWidth="2" opacity="0.5" />
                                    <text x="50" y="58" textAnchor="middle" fill="white" fontSize="24" fontWeight="bold">{index + 1}</text>
                                </svg>
                            </div>

                            {/* Ribbon Badge */}
                            <div className="relative w-16 h-20">
                                <svg className="w-full h-full" viewBox="0 0 80 100">
                                    <path d="M10,10 L70,10 L70,60 L40,80 L10,60 Z" fill={color.value} />
                                    <path d="M10,10 L70,10 L70,60 L40,80 L10,60 Z" fill="white" opacity="0.2" />
                                    <circle cx="40" cy="35" r="15" fill="white" opacity="0.3" />
                                    <text x="40" y="42" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold">★</text>
                                </svg>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Avatar Elements */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Avatar & Profile Elements</h5>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                    {colors.slice(0, 6).map((color, index) => (
                        <div key={index} className="flex flex-col items-center gap-2">
                            <div className="relative">
                                {/* Circular Avatar */}
                                <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg" style={{ backgroundColor: color.value }}>
                                    {String.fromCharCode(65 + index)}
                                </div>
                                {/* Status Indicator */}
                                <div className="absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white dark:border-gray-800" style={{ backgroundColor: index % 2 === 0 ? '#10b981' : '#f59e0b' }}></div>
                            </div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">User {index + 1}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* 3D-Style Elements */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">3D-Style Illustrations</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* 3D Card */}
                    <div className="relative h-48 rounded-xl overflow-hidden" style={{ backgroundColor: hexToRgba(getColor(0), 0.05) }}>
                        <svg className="w-full h-full" viewBox="0 0 200 192">
                            {/* Card with shadow */}
                            <defs>
                                <linearGradient id="card-3d-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor={getColor(0)} />
                                    <stop offset="100%" stopColor={hexToRgba(getColor(0), 0.7)} />
                                </linearGradient>
                            </defs>
                            {/* Shadow */}
                            <rect x="25" y="105" width="140" height="80" rx="8" fill="black" opacity="0.1" transform="skewY(-5)" />
                            {/* Main card */}
                            <rect x="30" y="50" width="140" height="90" rx="8" fill="url(#card-3d-grad)" />
                            {/* Card details */}
                            <rect x="40" y="60" width="40" height="25" rx="4" fill="white" opacity="0.3" />
                            <rect x="40" y="95" width="80" height="8" rx="4" fill="white" opacity="0.2" />
                            <rect x="40" y="110" width="60" height="8" rx="4" fill="white" opacity="0.2" />
                        </svg>
                    </div>

                    {/* 3D Cylinder */}
                    <div className="relative h-48 rounded-xl overflow-hidden" style={{ backgroundColor: hexToRgba(getColor(1), 0.05) }}>
                        <svg className="w-full h-full" viewBox="0 0 200 192">
                            <defs>
                                <linearGradient id="cylinder-grad-top" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor={hexToRgba(getColor(1), 0.8)} />
                                    <stop offset="50%" stopColor={getColor(1)} />
                                    <stop offset="100%" stopColor={hexToRgba(getColor(1), 0.8)} />
                                </linearGradient>
                                <linearGradient id="cylinder-grad-side" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor={getColor(1)} />
                                    <stop offset="100%" stopColor={hexToRgba(getColor(1), 0.6)} />
                                </linearGradient>
                            </defs>
                            {/* Cylinder body */}
                            <rect x="60" y="60" width="80" height="90" fill="url(#cylinder-grad-side)" />
                            {/* Bottom ellipse */}
                            <ellipse cx="100" cy="150" rx="40" ry="12" fill={hexToRgba(getColor(1), 0.5)} />
                            {/* Top ellipse */}
                            <ellipse cx="100" cy="60" rx="40" ry="12" fill="url(#cylinder-grad-top)" />
                        </svg>
                    </div>

                    {/* 3D Sphere */}
                    <div className="relative h-48 rounded-xl overflow-hidden" style={{ backgroundColor: hexToRgba(getColor(2), 0.05) }}>
                        <svg className="w-full h-full" viewBox="0 0 200 192">
                            <defs>
                                <radialGradient id="sphere-grad">
                                    <stop offset="0%" stopColor="white" stopOpacity="0.8" />
                                    <stop offset="30%" stopColor={getColor(2)} stopOpacity="0.9" />
                                    <stop offset="100%" stopColor={hexToRgba(getColor(2), 0.5)} />
                                </radialGradient>
                            </defs>
                            {/* Shadow */}
                            <ellipse cx="100" cy="170" rx="50" ry="10" fill="black" opacity="0.15" />
                            {/* Sphere */}
                            <circle cx="100" cy="96" r="50" fill="url(#sphere-grad)" />
                            {/* Highlight */}
                            <circle cx="85" cy="80" r="15" fill="white" opacity="0.4" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Logo & Brand Marks */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Logo & Brand Marks</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {colors.slice(0, 4).map((color, index) => (
                        <div key={index} className="aspect-square rounded-xl flex items-center justify-center p-6" style={{ backgroundColor: hexToRgba(color.value, 0.1) }}>
                            <svg className="w-full h-full" viewBox="0 0 100 100">
                                {index === 0 && (
                                    <>
                                        <circle cx="50" cy="50" r="35" fill="none" stroke={color.value} strokeWidth="6" />
                                        <path d="M50,30 L65,50 L50,70 L35,50 Z" fill={color.value} />
                                    </>
                                )}
                                {index === 1 && (
                                    <>
                                        <rect x="25" y="25" width="50" height="50" rx="8" fill={color.value} />
                                        <circle cx="50" cy="50" r="15" fill="white" opacity="0.9" />
                                    </>
                                )}
                                {index === 2 && (
                                    <>
                                        <polygon points="50,20 80,70 20,70" fill={color.value} />
                                        <polygon points="50,35 65,60 35,60" fill="white" opacity="0.7" />
                                    </>
                                )}
                                {index === 3 && (
                                    <>
                                        <path d="M50,20 Q70,30 70,50 Q70,70 50,80 Q30,70 30,50 Q30,30 50,20 Z" fill={color.value} />
                                        <circle cx="50" cy="50" r="8" fill="white" />
                                    </>
                                )}
                            </svg>
                        </div>
                    ))}
                </div>
            </div>

            {/* Brand Application Examples */}
            <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-lg p-6">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Illustration Applications</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                        <h6 className="font-medium text-gray-800 dark:text-gray-200">Design Applications</h6>
                        <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                            <li>• Hero sections and landing pages</li>
                            <li>• Empty states and error pages</li>
                            <li>• Onboarding flow illustrations</li>
                            <li>• Feature explanation graphics</li>
                            <li>• Social media and marketing assets</li>
                        </ul>
                    </div>
                    <div className="space-y-2">
                        <h6 className="font-medium text-gray-800 dark:text-gray-200">Technical Considerations</h6>
                        <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                            <li>• SVG format for scalability</li>
                            <li>• Consistent stroke weights (1.5-2px)</li>
                            <li>• Limited color palette usage</li>
                            <li>• Dark mode compatibility</li>
                            <li>• Accessibility-compliant contrast</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};